import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, 
  Clock, 
  CheckCircle2, 
  Calendar, 
  AlertTriangle,
  AlertCircle,
  Plus,
  ArrowRight,
  MapPin,
  Navigation,
  User,
  Bell,
  Timer,
  X,
  Zap,
  ChevronDown,
  LogIn,
  LogOut,
  Package,
  FileText,
  Wrench
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatDate, getStatusColor, getStatusStyle, getPriorityColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [urgentJobs, setUrgentJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [dueReminders, setDueReminders] = useState([]);
  const [personalReminders, setPersonalReminders] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [rejectedReports, setRejectedReports] = useState([]);
  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const [pendingFieldReportApprovals, setPendingFieldReportApprovals] = useState(0);
  const [pendingMaintenanceApprovals, setPendingMaintenanceApprovals] = useState(0);
  const [scopedToUser, setScopedToUser] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check alert permissions (default to true if not set)
  const canSeeAlert = (alertType) => {
    return user?.permissions?.[alertType] !== false;
  };

  const fetchDashboardData = useCallback(async () => {
    // Fire all 4 independent Dashboard API calls in PARALLEL and use
    // allSettled so a single failing call only blanks its own section
    // instead of taking down the whole Dashboard. Each call keeps its
    // per-section fallback (empty list / zero count / prior state) so
    // the page stays usable even if one endpoint is broken.
    const [summaryRes, mdRes, frRes, mRes] = await Promise.allSettled([
      axios.get(`${API}/dashboard/summary`),
      axios.get(`${API}/equipment/maintenance-due`),
      axios.get(`${API}/field-reports/review-queue/stats`),
      axios.get(`${API}/maintenance/pending-approval/count`),
    ]);

    // ─── Dashboard summary (main payload) ───────────────────────────
    if (summaryRes.status === 'fulfilled') {
      const data = summaryRes.value.data;

      // Helper: filter out internal/equipment jobs
      const filterOutInternalJobs = (jobs) => {
        return (jobs || []).filter(job => {
          const isInternal = job.is_internal === true ||
            job.client_name === 'Internal' ||
            job.client_name?.toLowerCase() === 'internal' ||
            job.job_type === 'internal' ||
            job.service_type?.toLowerCase().includes('equipment') ||
            job.job_title?.toLowerCase().includes('truck service') ||
            job.job_title?.toLowerCase().includes('equipment') ||
            job.notes?.toLowerCase().includes('equipment:');
          return !isInternal;
        });
      };

      setStats(data.stats);
      setScopedToUser(!!data.scoped_to_user);
      setUrgentJobs(filterOutInternalJobs(data.urgent_jobs));
      setPendingJobs(filterOutInternalJobs(data.pending_jobs));
      setTodaysJobs(filterOutInternalJobs(data.today_jobs));
      setUpcomingJobs(filterOutInternalJobs(data.upcoming_jobs).slice(0, 5));
      setDueReminders(data.reminders_811 || []);
      setPersonalReminders(data.personal_reminders || []);

      // "My Jobs" — pending/scheduled client jobs only
      const myClientJobs = filterOutInternalJobs(data.my_jobs).filter(job =>
        job.status === 'Pending' || job.status === 'Scheduled' || job.status === 'Ready to schedule'
      );
      setAssignedJobs(myClientJobs);
      setRejectedReports(data.rejected_notifications || []);
    } else {
      // Only the main summary failing warrants a console error — the
      // three sidecar counts fail silently by design.
      console.error('Failed to fetch dashboard summary:', summaryRes.reason);
    }

    // ─── Equipment maintenance due (sidecar #1) ─────────────────────
    if (mdRes.status === 'fulfilled') {
      setMaintenanceDue(mdRes.value.data || []);
    } else {
      setMaintenanceDue([]);
    }

    // ─── Pending Field Report approvals (sidecar #2) ────────────────
    if (frRes.status === 'fulfilled') {
      setPendingFieldReportApprovals(frRes.value.data?.total || 0);
    } else {
      setPendingFieldReportApprovals(0);
    }

    // ─── Pending Maintenance approvals (sidecar #3) ─────────────────
    if (mRes.status === 'fulfilled') {
      setPendingMaintenanceApprovals(mRes.value.data?.count || 0);
    } else {
      setPendingMaintenanceApprovals(0);
    }

    setLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh when user returns to app
  useAutoRefresh(fetchDashboardData, { refreshOnReturn: true });

  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    timeZone: 'America/New_York'
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Dashboard
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-slate-500">{today}</p>
            {scopedToUser && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium"
                title="You only see jobs you're assigned to. Ask an admin to enable 'View All Jobs' if you need full visibility."
                data-testid="dashboard-scoped-badge"
              >
                <User className="w-3 h-3" />
                Showing your jobs only
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Time Tracker — workflow tool */}
          <Link to="/field-tracker-v2">
            <Button
              size="sm"
              className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold"
              data-testid="start-tracking-btn"
            >
              <Timer className="w-4 h-4 mr-2" />
              Time Tracker
            </Button>
          </Link>

          {/* Equipment dropdown — workflow tool */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold"
                data-testid="dashboard-quick-equipment-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Equipment
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem
                onClick={() => navigate('/maintenance?quickAction=checkout')}
                className="cursor-pointer py-3"
                data-testid="dashboard-quick-checkout"
              >
                <LogOut className="w-5 h-5 mr-3 text-green-600" />
                <div>
                  <div className="font-medium">Quick Check Out</div>
                  <div className="text-xs text-slate-500">Grab equipment for the day</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/maintenance?quickAction=checkin')}
                className="cursor-pointer py-3"
                data-testid="dashboard-quick-checkin"
              >
                <LogIn className="w-5 h-5 mr-3 text-blue-600" />
                <div>
                  <div className="font-medium">Quick Check In</div>
                  <div className="text-xs text-slate-500">Return equipment</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/maintenance')}
                className="cursor-pointer py-3 border-t"
                data-testid="dashboard-view-equipment"
              >
                <Package className="w-5 h-5 mr-3 text-slate-600" />
                <div>
                  <div className="font-medium">View All Equipment</div>
                  <div className="text-xs text-slate-500">Manage inventory</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Secondary creation buttons — outline neutral (desktop only) */}
          <div className="hidden md:flex items-center gap-3">
            {(isAdmin() || hasPermission('create_proposals')) && (
              <Link to="/opportunities?new=true">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="new-opportunity-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Opportunity
                </Button>
              </Link>
            )}
            {(isAdmin() || hasPermission('create_proposals')) && (
              <Link to="/proposals?new=true">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="new-proposal-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Proposal
                </Button>
              </Link>
            )}
            {(isAdmin() || hasPermission('create_jobs')) && (
              <Link to="/jobs/new">
                <Button
                  size="sm"
                  className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                  data-testid="new-job-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Job
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Today's Jobs</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{stats?.todays_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-600">In Progress</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">{stats?.in_progress || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Completed Week</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">{stats?.completed_this_week || 0}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-purple-600">Scheduled</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">{stats?.scheduled || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Banners */}
      <div className="space-y-3">
        {/* Urgent Jobs Banner */}
        {stats?.urgent_jobs > 0 && canSeeAlert('alert_urgent_jobs') && (
          <div 
            className="flex items-center justify-between p-4 bg-red-100 border border-red-300 rounded-lg"
            data-testid="urgent-jobs-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <span className="font-semibold text-red-800">
                {stats.urgent_jobs} urgent job{stats.urgent_jobs > 1 ? 's' : ''} need{stats.urgent_jobs === 1 ? 's' : ''} attention
                {/* Fix (2026-08-30, polish item #4): lightweight count
                    badge instead of a full itemized equipment list —
                    a job can be both Urgent and in Today's/Upcoming,
                    so a full card list here would duplicate rows. */}
                {urgentJobs.filter(j => j.assigned_equipment_ids?.length > 0).length > 0 && (
                  <span className="text-red-600 font-normal" data-testid="urgent-jobs-equipment-count">
                    {' '}· {urgentJobs.filter(j => j.assigned_equipment_ids?.length > 0).length} with equipment assigned
                  </span>
                )}
              </span>
            </div>
            <Link 
              to="/jobs?priority=Urgent" 
              className="text-red-700 hover:text-red-800 font-medium flex items-center gap-1"
            >
              View Jobs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Pending Jobs Banner */}
        {stats?.pending_jobs > 0 && canSeeAlert('alert_pending_jobs') && (
          <div 
            className="flex items-center justify-between p-4 bg-yellow-100 border border-yellow-300 rounded-lg"
            data-testid="pending-jobs-banner"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <span className="font-semibold text-yellow-800">
                {stats.pending_jobs} pending job{stats.pending_jobs > 1 ? 's' : ''} need{stats.pending_jobs === 1 ? 's' : ''} to be addressed
                {pendingJobs.filter(j => j.assigned_equipment_ids?.length > 0).length > 0 && (
                  <span className="text-yellow-700 font-normal" data-testid="pending-jobs-equipment-count">
                    {' '}· {pendingJobs.filter(j => j.assigned_equipment_ids?.length > 0).length} with equipment assigned
                  </span>
                )}
              </span>
            </div>
            <Link 
              to="/jobs?status=Pending" 
              className="text-yellow-700 hover:text-yellow-800 font-medium flex items-center gap-1"
            >
              View Jobs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Assigned Jobs Banner */}
        {assignedJobs.length > 0 && canSeeAlert('alert_assigned_jobs') && (
          <div 
            className="flex items-center justify-between p-4 bg-blue-100 border border-blue-300 rounded-lg"
            data-testid="assigned-jobs-banner"
          >
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-blue-800">
                You have {assignedJobs.length} job{assignedJobs.length > 1 ? 's' : ''} assigned to you
              </span>
            </div>
            <Link 
              to="/my-jobs" 
              className="text-blue-700 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              View My Jobs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Pending Field Report Approvals Banner — only shown to assigned approvers */}
        {pendingFieldReportApprovals > 0 && (
          <Link
            to="/review-queue"
            className="flex items-center justify-between p-4 bg-purple-100 border border-purple-300 rounded-lg hover:bg-purple-200 transition-colors"
            data-testid="pending-field-reports-banner"
          >
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-purple-600" />
              <span className="font-semibold text-purple-800">
                You have {pendingFieldReportApprovals} field report{pendingFieldReportApprovals > 1 ? 's' : ''} waiting for your approval
              </span>
            </div>
            <span className="text-purple-700 hover:text-purple-800 font-medium flex items-center gap-1">
              Review <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        )}

        {/* Pending Maintenance Approvals Banner — only shown to maintenance managers */}
        {pendingMaintenanceApprovals > 0 && (
          <Link
            to="/maintenance"
            className="flex items-center justify-between p-4 bg-emerald-100 border border-emerald-300 rounded-lg hover:bg-emerald-200 transition-colors"
            data-testid="pending-maintenance-banner"
          >
            <div className="flex items-center gap-3">
              <Wrench className="w-6 h-6 text-emerald-600" />
              <span className="font-semibold text-emerald-800">
                You have {pendingMaintenanceApprovals} maintenance form{pendingMaintenanceApprovals > 1 ? 's' : ''} waiting for your approval
              </span>
            </div>
            <span className="text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1">
              Review <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        )}

        {/* Equipment Maintenance Due Banner */}
        {maintenanceDue.length > 0 && (
          <Link
            to="/maintenance"
            className="block p-4 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors"
            data-testid="maintenance-due-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-orange-600 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-orange-800">
                    {maintenanceDue.filter(m => m.overdue).length > 0 && (
                      <span className="text-red-700">
                        {maintenanceDue.filter(m => m.overdue).length} overdue
                      </span>
                    )}
                    {maintenanceDue.filter(m => m.overdue).length > 0 && maintenanceDue.filter(m => !m.overdue).length > 0 && ' · '}
                    {maintenanceDue.filter(m => !m.overdue).length > 0 && (
                      <>{maintenanceDue.filter(m => !m.overdue).length} maintenance task{maintenanceDue.filter(m => !m.overdue).length > 1 ? 's' : ''} due soon</>
                    )}
                    {maintenanceDue.filter(m => m.overdue).length > 0 && maintenanceDue.filter(m => !m.overdue).length === 0 && (
                      <> maintenance task{maintenanceDue.filter(m => m.overdue).length > 1 ? 's' : ''}</>
                    )}
                  </span>
                  <ul className="text-sm text-orange-700 mt-1 space-y-0.5">
                    {maintenanceDue.slice(0, 3).map(m => (
                      <li key={`${m.source}-${m.equipment_id}-${m.field_id}`} className="flex items-center gap-2 flex-wrap">
                        {m.asset_number && (
                          <span className="font-mono text-xs text-amber-700">{m.asset_number}</span>
                        )}
                        <span className="font-medium">{m.equipment_name}</span>
                        <span className="text-orange-500">·</span>
                        <span>{m.field_label}</span>
                        <span className="text-orange-500">·</span>
                        <span className={m.overdue ? 'text-red-700 font-bold' : ''}>
                          {m.overdue
                            ? `${Math.abs(m.days_until)} day${Math.abs(m.days_until) === 1 ? '' : 's'} overdue`
                            : m.days_until === 0 ? 'due today' : `due in ${m.days_until} day${m.days_until === 1 ? '' : 's'}`}
                        </span>
                        {m.assignee_name && (
                          <span className="text-xs text-slate-500">— {m.assignee_name}</span>
                        )}
                      </li>
                    ))}
                    {maintenanceDue.length > 3 && (
                      <li className="text-xs text-orange-600 italic">+ {maintenanceDue.length - 3} more</li>
                    )}
                  </ul>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-orange-700 flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* Rejected Field Reports Banner */}
        {rejectedReports.length > 0 && (
          <div 
            className="flex items-center justify-between p-4 bg-orange-100 border border-orange-300 rounded-lg"
            data-testid="rejected-reports-banner"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-orange-600" />
              <div>
                <span className="font-semibold text-orange-800">
                  {rejectedReports.length} field report{rejectedReports.length > 1 ? 's' : ''} rejected - needs revision
                </span>
                <p className="text-sm text-orange-700">
                  {rejectedReports[0]?.message}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link 
                to="/field-reports" 
                className="text-orange-700 hover:text-orange-800 font-medium flex items-center gap-1"
              >
                View Reports <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/notifications/mark-all-read?type=field_report_rejected`);
                    setRejectedReports([]);
                  } catch (e) {
                    console.error('Failed to dismiss notifications:', e);
                  }
                }}
                className="ml-2 p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded"
                title="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* 811 Ticket Reminders Banner */}
        {dueReminders.length > 0 && canSeeAlert('alert_811_reminders') && (
          <Link 
            to="/811-info?filter=reminders"
            className="block"
            data-testid="811-reminders-banner"
          >
            <div className="flex items-center justify-between p-4 bg-green-100 border border-green-300 rounded-lg hover:bg-green-200 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 text-green-600" />
                <div>
                  <span className="font-semibold text-green-800">
                    {dueReminders.length} 811 ticket reminder{dueReminders.length > 1 ? 's' : ''} due
                  </span>
                  {dueReminders.length === 1 && dueReminders[0].ticket_number && (
                    <span className="text-green-700 text-sm ml-2">
                      (Ticket #{dueReminders[0].ticket_number})
                    </span>
                  )}
                </div>
              </div>
              <span className="text-green-700 hover:text-green-800 font-medium flex items-center gap-1">
                View Reminders <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        )}
      </div>

      {/* Today's Jobs and Upcoming Jobs - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Jobs */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg font-['Barlow_Condensed']">Today's Jobs</CardTitle>
            <Link to="/calendar" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
              View Calendar <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {todaysJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No jobs scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaysJobs.map(job => (
                  <Link 
                    key={job.id} 
                    to={`/jobs/${job.id}`}
                    state={{ from: 'dashboard' }}
                    className="block p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-amber-500/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        job.status === 'Pending' ? 'bg-blue-500' : 
                        job.status === 'Scheduled' ? 'bg-amber-500' :
                        job.status === 'In Progress' ? 'bg-emerald-500' : 'bg-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{job.job_title}</h3>
                          {job.estimated_hours && (
                            <span className="text-xs text-slate-500 flex-shrink-0">~{job.estimated_hours}h</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {job.service_type && (
                            <Badge variant="outline" className="text-xs bg-white">{job.service_type}</Badge>
                          )}
                          <Badge className={`text-xs ${getStatusColor(job.status)}`} style={getStatusStyle(job.status) || undefined}>{job.status}</Badge>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-600 mt-2">
                          <User className="w-3 h-3" />
                          <span className="truncate">{job.client_name}</span>
                        </div>
                        {job.service_address && (
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{job.service_address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(job.date)}</span>
                        </div>
                        {job.assigned_equipment_ids && job.assigned_equipment_ids.length > 0 && (
                          <div className="flex items-start gap-1 text-xs text-slate-500 mt-1.5" data-testid={`today-job-equipment-${job.id}`}>
                            <Wrench className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
                            <span className="truncate">
                              {job.assigned_equipment_ids.map(eid => (job.assigned_equipment_snapshot || {})[eid] || eid).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Jobs */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg font-['Barlow_Condensed']">Upcoming Jobs</CardTitle>
            <Link to="/jobs" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="mb-3">No upcoming jobs scheduled</p>
                <Link to="/jobs/new">
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule a Job
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingJobs.map(job => (
                  <Link 
                    key={job.id} 
                    to={`/jobs/${job.id}`}
                    state={{ from: 'dashboard' }}
                    className="block p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-amber-500/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        job.priority === 'Urgent' ? 'bg-red-500' : 
                        job.priority === 'High' ? 'bg-amber-500' : 'bg-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{job.job_title}</h3>
                          {job.estimated_hours && (
                            <span className="text-xs text-slate-500 flex-shrink-0">~{job.estimated_hours}h</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {job.service_type && (
                            <Badge variant="outline" className="text-xs bg-white">{job.service_type}</Badge>
                          )}
                          <Badge className={`text-xs ${getStatusColor(job.status)}`} style={getStatusStyle(job.status) || undefined}>{job.status}</Badge>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-600 mt-2">
                          <User className="w-3 h-3" />
                          <span className="truncate">{job.client_name}</span>
                        </div>
                        {job.service_address && (
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{job.service_address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(job.date)}</span>
                        </div>
                        {job.assigned_equipment_ids && job.assigned_equipment_ids.length > 0 && (
                          <div className="flex items-start gap-1 text-xs text-slate-500 mt-1.5" data-testid={`upcoming-job-equipment-${job.id}`}>
                            <Wrench className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
                            <span className="truncate">
                              {job.assigned_equipment_ids.map(eid => (job.assigned_equipment_snapshot || {})[eid] || eid).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Personal Reminders Widget */}
      {personalReminders.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              My Reminders
            </CardTitle>
            <Link to="/reminders" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {personalReminders.map(reminder => (
                <Link 
                  key={reminder.id} 
                  to="/reminders"
                  className={`block p-3 rounded-lg border transition-colors hover:border-amber-400 ${
                    reminder.priority === 'High' 
                      ? 'bg-red-50 border-red-200' 
                      : reminder.priority === 'Medium'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{reminder.title}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(reminder.date)}
                        </span>
                        {reminder.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {reminder.time}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge 
                      className={`text-xs ${
                        reminder.category === 'Job' ? 'bg-amber-100 text-amber-800' :
                        reminder.category === '811' ? 'bg-red-100 text-red-800' :
                        reminder.category === 'Equipment' ? 'bg-emerald-100 text-emerald-800' :
                        reminder.category === 'Meeting' ? 'bg-blue-100 text-blue-800' :
                        'bg-indigo-100 text-indigo-800'
                      }`}
                    >
                      {reminder.category}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
